import React, { useMemo, useState } from 'react';
import { Card, CardBody, Col, Container, Row, Spinner, Alert, Button } from 'reactstrap';
import Breadcrumbs from 'components/Common/Breadcrumb';
import TableContainer from 'components/Common/TableContainer';
import {
  usePaginatedListQuery,
  useUpdatePatchMutation,
  useDeleteMutation,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { ColumnDef } from '@tanstack/react-table';
import SharedCategoryCreateOrUpdate from 'components/Form/Catalog/SharedCategoryCreateOrUpdate';

interface SharedCategory {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
}

const SharedCategoriesList: React.FC = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string | undefined>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined);

  const { data, isLoading, isError, error, refetch } = usePaginatedListQuery<SharedCategory>(
    API_ENDPOINTS.SAAS.SHARED_CATEGORIES.BASE,
    {
      pageNumber,
      pageSize,
      search: search || undefined,
      sortBy,
      sortDirection,
    },
  );

  const categories: SharedCategory[] = useMemo(() => {
    return data?.items ?? [];
  }, [data]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (isModalOpen) {
      setSelectedCategoryId(undefined);
    }
  };

  const handleAddNew = () => {
    setSelectedCategoryId(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (id: string) => {
    setSelectedCategoryId(id);
    setIsModalOpen(true);
  };

  const handleModalSuccess = () => {
    refetch();
  };

  const columns: ColumnDef<SharedCategory>[] = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
      },
      {
        header: 'Description',
        accessorKey: 'description',
      },
      {
        header: 'Status',
        accessorKey: 'isActive',
        cell: ({ row }) => <span>{row.original.isActive ? 'Active' : 'Inactive'}</span>,
      },
      {
        header: 'Actions',
        id: 'actions',
        cell: ({ row }) => {
          const { id, isActive } = row.original;
          const { mutate: toggleStatus } = useUpdatePatchMutation(
            `${API_ENDPOINTS.SAAS.SHARED_CATEGORIES.BASE}/${id}`,
            API_ENDPOINTS.SAAS.SHARED_CATEGORIES.BASE,
          );

          const { mutate: deleteCategory } = useDeleteMutation(
            `${API_ENDPOINTS.SAAS.SHARED_CATEGORIES.BASE}/${id}`,
            API_ENDPOINTS.SAAS.SHARED_CATEGORIES.BASE,
          );

          const handleToggleStatus = () => {
            if (
              window.confirm(
                `Are you sure you want to ${isActive ? 'deactivate' : 'activate'} this category?`,
              )
            ) {
              toggleStatus({ data: { isActive: !isActive }, params: { id } });
            }
          };

          const handleDelete = () => {
            if (window.confirm('Are you sure you want to delete this category?')) {
              deleteCategory(id);
            }
          };

          return (
            <div className="d-flex gap-2 align-items-center">
              <Button size="sm" color="primary" outline onClick={() => handleEdit(id)}>
                Edit
              </Button>
              <i
                className={`bx ${isActive ? 'bx-check-circle' : 'bx-x-circle'} ${
                  isActive ? 'text-success' : 'text-warning'
                } cursor-pointer`}
                title={isActive ? 'Deactivate' : 'Activate'}
                style={{ fontSize: '1.2rem' }}
                onClick={handleToggleStatus}
              ></i>
              <i
                className="bx bx-trash text-muted cursor-pointer"
                title="Delete"
                style={{ fontSize: '1.2rem' }}
                onClick={handleDelete}
              ></i>
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Shared Categories" breadcrumbItem="List" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                {isLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : isError ? (
                  <Alert color="danger">
                    {error?.message || 'Failed to load categories. Please try again later.'}
                  </Alert>
                ) : (
                  <>
                    <div className="mb-3">
                      <Button color="success" onClick={handleAddNew}>
                        Add Category
                      </Button>
                    </div>
                    <TableContainer
                      columns={columns}
                      data={categories}
                      isGlobalFilter={true}
                      isAddButton={false}
                      isPagination={true}
                      isCustomPageSize={true}
                      buttonClass="btn btn-success btn-rounded"
                      buttonName="Add Category"
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-borderless"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      paginationClass="pagination"
                      SearchPlaceholder="Search categories..."
                    />

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <small className="text-muted">
                        Showing {categories.length > 0 ? (pageNumber - 1) * pageSize + 1 : 0} to{' '}
                        {Math.min(pageNumber * pageSize, data?.totalCount || 0)} of{' '}
                        {data?.totalCount || 0} items
                      </small>

                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          outline
                          color="primary"
                          onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                          disabled={!data?.hasPreviousPage || isLoading}
                        >
                          Previous
                        </Button>
                        <span className="align-self-center small">
                          Page {data?.pageNumber || 1} of {data?.totalPages || 1}
                        </span>
                        <Button
                          size="sm"
                          outline
                          color="primary"
                          onClick={() => setPageNumber((p) => p + 1)}
                          disabled={!data?.hasNextPage || isLoading}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <SharedCategoryCreateOrUpdate
        isOpen={isModalOpen}
        toggle={toggleModal}
        categoryId={selectedCategoryId}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default SharedCategoriesList;
